// Patch 009.2 — ProjectSwitcher now reads from /api/projects.
//
// Patch 008.6/008.7 used a hardcoded single Oakridge entry plus a fallback
// for unrecognized URLs. Patch 009.2 replaces that with a real fetch so
// every newly-created project (via NewProjectModal) shows up in the
// dropdown without code edits.
//
// Failure modes:
//   - Loading → render the URL projectId as a placeholder option so the
//     <select value> resolves until the fetch lands.
//   - Fetch error → fall back to the loading placeholder + log to console.
//   - URL projectId not in fetched list → surface as "Project <prefix>…"
//     (e.g. for the synthetic test fixture that's not in production lists).
//
// onChange is wired: selecting a project navigates to that project's
// /plan/scope path (Patch 015.3 — Plan lands on Scope, not the demoted
// Command Board). The hardcoded "+ Add New Project" disabled option
// from 008.7 is removed — that button moved to the header in Patch 008.7
// and now opens the NewProjectModal for real in Patch 009.2.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, type ProjectListEntry } from '../lib/api'

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; projects: ProjectListEntry[] }
  | { kind: 'error'; message: string }

let cached: Phase = { kind: 'loading' }
let inFlight: Promise<ProjectListEntry[]> | null = null
const subscribers = new Set<(p: Phase) => void>()

function publish(p: Phase): void {
  cached = p
  for (const fn of subscribers) fn(p)
}

async function loadProjects(): Promise<void> {
  if (inFlight) return
  inFlight = api
    .projects.list()
    .then((res) => {
      publish({ kind: 'ready', projects: res.projects })
      return res.projects
    })
    .catch((err: Error) => {
      console.error('ProjectSwitcher: list failed', err)
      publish({ kind: 'error', message: err.message })
      throw err
    })
    .finally(() => {
      inFlight = null
    })
}

/** Force-refresh the project list. Called by Header after a successful
 *  NewProjectModal create (or DeleteProjectModal delete in Patch 010.0.4)
 *  so the dropdown reflects the new state. */
export function refreshProjectList(): void {
  cached = { kind: 'loading' }
  for (const fn of subscribers) fn(cached)
  void loadProjects()
}

/** Patch 010.0.4 — hook returning the current project's full
 *  ProjectListEntry (or null if not yet loaded / not found). Subscribes
 *  to the same publisher the switcher uses so the Header can render the
 *  Delete button conditionally on is_synthetic without a separate fetch. */
export function useCurrentProject(projectId: string | undefined): ProjectListEntry | null {
  const [phase, setPhase] = useState<Phase>(cached)
  useEffect(() => {
    subscribers.add(setPhase)
    if (cached.kind === 'loading') void loadProjects()
    return () => {
      subscribers.delete(setPhase)
    }
  }, [])
  if (!projectId) return null
  if (phase.kind !== 'ready') return null
  return phase.projects.find((p) => p.id === projectId) ?? null
}

/** Patch 010.0.4 — return the first non-synthetic, non-current project's id
 *  for post-delete navigation. Falls back to the first project, or null if
 *  no other projects exist. */
export function getNextProjectId(currentProjectId: string): string | null {
  if (cached.kind !== 'ready') return null
  const others = cached.projects.filter((p) => p.id !== currentProjectId)
  const nonSynthetic = others.find((p) => p.is_synthetic !== 1)
  if (nonSynthetic) return nonSynthetic.id
  return others[0]?.id ?? null
}

export function ProjectSwitcher(props: { projectId?: string }): JSX.Element {
  const [phase, setPhase] = useState<Phase>(cached)
  const navigate = useNavigate()

  useEffect(() => {
    subscribers.add(setPhase)
    if (cached.kind === 'loading') void loadProjects()
    return () => {
      subscribers.delete(setPhase)
    }
  }, [])

  const projects = phase.kind === 'ready' ? phase.projects : []
  const value = props.projectId ?? (projects[0]?.id ?? '')
  const knownIds = new Set(projects.map((p) => p.id))
  const showFallback = props.projectId && !knownIds.has(props.projectId)

  return (
    <select
      className="hcc-proj-select"
      value={value}
      onChange={(e) => {
        const target = e.target.value
        if (!target) return
        navigate(`/projects/${target}/plan/scope`)
      }}
      aria-label="Project"
      data-testid="project-switcher"
    >
      {phase.kind === 'loading' && projects.length === 0 ? (
        <option value={value}>Loading projects…</option>
      ) : null}
      {projects.map((p) => (
        <option key={p.id} value={p.id}>
          {p.is_synthetic === 1 ? 'SYNTHETIC · ' : ''}
          {p.display_name}
        </option>
      ))}
      {showFallback && props.projectId ? (
        <option value={props.projectId}>{`Project ${props.projectId.slice(0, 8)}…`}</option>
      ) : null}
    </select>
  )
}
